import { FormEvent, useState } from 'react';
import type { ExpenseCostSplit } from '../../api';
import { Aircraft, Issue, IssueAttachment } from '../../types';
import { useFeedback } from '../../hooks/useFeedback';
import { useSubmitGuard } from '../../hooks/useSubmitGuard';
import {
  formatIssuePerformerLabel,
  issuePerformerError,
  issuePerformerFromSaved,
} from '../../utils/aircraftMaintenance';
import { isLegacyRepairSplit, parseIssueCost } from '../../utils/issueDisplay';
import {
  OWNER_SPLITS,
  hoursFlightIdsPayload,
  ownerSplitError,
  ownerSplitFromSaved,
  ownerSplitRequest,
} from '../../utils/expenseSplit';
import IssueAttachmentsBlock from '../IssueAttachmentsBlock/IssueAttachmentsBlock';
import IssueResolveFields from '../IssueResolveFields/IssueResolveFields';
import ModalPortal from '../modal/ModalPortal';
import '../modal/ModalBase.css';

const todayIso = () => new Date().toISOString().split('T')[0];

const toDateInput = (value?: string | null) => {
  if (!value) return todayIso();
  const text = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(text)) return text.slice(0, 10);
  return todayIso();
};

export type EditIssuePayload = {
  description: string;
  severity: string;
  issue_date: string;
  resolved_date?: string;
  resolved_by?: string;
  cost?: number;
  cost_split?: ExpenseCostSplit;
  payer_ids?: number[];
  payer_shares?: number[];
  flight_ids?: number[];
  comment?: string;
};

type Props = {
  issue: Issue;
  aircraft?: Aircraft | null;
  canEditResolution?: boolean;
  filesUploading?: boolean;
  onClose: () => void;
  onConfirm: (data: EditIssuePayload) => void | Promise<void>;
  onOpenFile?: (file: IssueAttachment, files: IssueAttachment[]) => void;
  onDeleteFile?: (file: IssueAttachment) => void;
  onAddFiles?: (files: File[]) => void;
};

const EditIssueModal = ({
  issue,
  aircraft,
  canEditResolution = false,
  filesUploading = false,
  onClose,
  onConfirm,
  onOpenFile,
  onDeleteFile,
  onAddFiles,
}: Props) => {
  const { showError, feedbackNode } = useFeedback();
  const { submitting, runSubmit } = useSubmitGuard();
  const fromFlight = Boolean(issue.flight_id);
  const resolved = Boolean(issue.is_resolved);
  const initialPerformer = issuePerformerFromSaved(issue.resolved_by, aircraft);
  const hoursSplit = isLegacyRepairSplit(issue.cost_split);
  const initialSplit = hoursSplit
    ? { costSplit: 'hours' as ExpenseCostSplit, manualShares: {} }
    : ownerSplitFromSaved(issue.cost_split, issue.payer_ids, issue.payer_shares);
  const [form, setForm] = useState({
    description: issue.description || '',
    severity: issue.severity || 'medium',
    issue_date: toDateInput(issue.issue_date || issue.flight_date),
    resolved_date: toDateInput(issue.resolved_at),
    performerType: initialPerformer.type,
    performerName: initialPerformer.name,
    cost: issue.cost != null ? String(issue.cost) : '0',
    cost_split: initialSplit.costSplit,
    comment: issue.comment || '',
  });
  const [manualShares, setManualShares] = useState<Record<number, string>>(
    () => initialSplit.manualShares
  );
  const [flightIds, setFlightIds] = useState<number[]>(() => issue.flight_ids || []);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    if (!form.description.trim()) {
      showError('Укажите описание');
      return;
    }
    if (!form.issue_date) {
      showError('Укажите дату неисправности');
      return;
    }
    if (!fromFlight && form.issue_date > todayIso()) {
      showError('Дата неисправности не может быть позже сегодняшнего дня');
      return;
    }

    const payload: EditIssuePayload = {
      description: form.description.trim(),
      severity: form.severity || 'medium',
      issue_date: form.issue_date,
      comment: form.comment.trim(),
    };

    if (resolved && canEditResolution) {
      if (!form.resolved_date) {
        showError('Укажите дату устранения');
        return;
      }
      const performerError = issuePerformerError(
        form.performerType,
        form.performerName
      );
      if (performerError) {
        showError(performerError);
        return;
      }
      const parsed = parseIssueCost(form.cost);
      if (!parsed.ok) {
        showError(parsed.error);
        return;
      }
      const split: ExpenseCostSplit =
        parsed.value > 0 ? form.cost_split : 'shares';
      if (split === 'hours' && flightIds.length === 0) {
        showError('Выберите полёты для пропорционального расчёта');
        return;
      }
      const splitError = ownerSplitError(split, manualShares);
      if (splitError) {
        showError(splitError);
        return;
      }
      payload.resolved_date = form.resolved_date;
      payload.resolved_by = formatIssuePerformerLabel(
        form.performerType,
        form.performerName
      );
      payload.cost = parsed.value;
      Object.assign(payload, ownerSplitRequest(split, manualShares));
      payload.flight_ids = hoursFlightIdsPayload(
        split,
        flightIds,
        issue.flight_ids
      );
    }

    void runSubmit(() => onConfirm(payload));
  };

  return (
    <ModalPortal>
      <div className="modal-overlay" onClick={onClose}>
        {feedbackNode}
        <form
          className="modal"
          onClick={(e) => e.stopPropagation()}
          onSubmit={handleSubmit}
        >
          <h3>Изменить неисправность</h3>
          <div className="modal-body">
            <label>Описание</label>
            <input
              type="text"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              required
            />
            <label>Степень важности</label>
            <select
              value={form.severity}
              onChange={(e) => setForm({ ...form, severity: e.target.value })}
            >
              <option value="low">Низкая</option>
              <option value="medium">Средняя</option>
              <option value="high">Высокая</option>
            </select>
            <label>Дата неисправности</label>
            <input
              type="date"
              value={form.issue_date}
              onChange={(e) => setForm({ ...form, issue_date: e.target.value })}
              max={todayIso()}
              required
              disabled={fromFlight}
            />
            {fromFlight ? (
              <p>Дата взята из полёта и не меняется</p>
            ) : null}
            <label>Комментарий</label>
            <textarea
              value={form.comment}
              onChange={(e) => setForm({ ...form, comment: e.target.value })}
              placeholder="Любая информация по этой неисправности..."
              rows={5}
            />
            {onOpenFile && onAddFiles && onDeleteFile ? (
              <IssueAttachmentsBlock
                files={issue.attachments || []}
                canEdit
                uploading={filesUploading}
                onOpen={onOpenFile}
                onDelete={onDeleteFile}
                onAdd={onAddFiles}
              />
            ) : null}

            {resolved && canEditResolution ? (
              <>
                <label>Дата устранения</label>
                <input
                  type="date"
                  value={form.resolved_date}
                  onChange={(e) =>
                    setForm({ ...form, resolved_date: e.target.value })
                  }
                />
                <IssueResolveFields
                  resolvedDate={form.resolved_date}
                  performerType={form.performerType}
                  performerName={form.performerName}
                  cost={form.cost}
                  costSplit={form.cost_split}
                  manualShares={manualShares}
                  flightIds={flightIds}
                  allowedSplits={
                    hoursSplit ? [...OWNER_SPLITS, 'hours'] : OWNER_SPLITS
                  }
                  onPerformerTypeChange={(performerType) =>
                    setForm({ ...form, performerType })
                  }
                  onPerformerNameChange={(performerName) =>
                    setForm({ ...form, performerName })
                  }
                  onCostChange={(cost) => setForm({ ...form, cost })}
                  onCostSplitChange={(cost_split) =>
                    setForm({ ...form, cost_split })
                  }
                  onManualSharesChange={setManualShares}
                  onFlightIdsChange={setFlightIds}
                />
              </>
            ) : null}
          </div>
          <div className="modal-actions">
            <button
              type="button"
              className="btn-cancel"
              onClick={onClose}
              disabled={submitting}
            >
              Отмена
            </button>
            <button type="submit" className="btn-save" disabled={submitting}>
              {submitting ? '…' : 'Сохранить'}
            </button>
          </div>
        </form>
      </div>
    </ModalPortal>
  );
};

export default EditIssueModal;
